// Copyright © 2026 ApexRuntime Labs by Shashi (https://github.com/spshah). All rights reserved.

import { transactionAPI } from './budget/transactionService';

// Test transaction templates with realistic data
const TEST_TRANSACTIONS = [
  // ─── EXPENSES ───────────────────────────────────────────
  // Housing & Utilities
  { category: 'Housing & Utilities', amount: 15000, type: 'expense', description: 'Monthly Rent Payment', daysAgo: 5 },
  { category: 'Housing & Utilities', amount: 1200, type: 'expense', description: 'Electricity Bill', daysAgo: 3 },
  { category: 'Housing & Utilities', amount: 800, type: 'expense', description: 'Water & Sewage', daysAgo: 7 },
  { category: 'Housing & Utilities', amount: 599, type: 'expense', description: 'Internet & Phone Bill', daysAgo: 2 },
  { category: 'Housing & Utilities', amount: 450, type: 'expense', description: 'Gas Bill', daysAgo: 10 },

  // Food & Dining
  { category: 'Food & Dining', amount: 2500, type: 'expense', description: 'Weekly Grocery Shopping', daysAgo: 1 },
  { category: 'Food & Dining', amount: 1800, type: 'expense', description: 'Costco Shopping', daysAgo: 14 },
  { category: 'Food & Dining', amount: 850, type: 'expense', description: 'Restaurant Dinner', daysAgo: 4 },
  { category: 'Food & Dining', amount: 320, type: 'expense', description: 'Breakfast Meeting', daysAgo: 6 },
  { category: 'Food & Dining', amount: 450, type: 'expense', description: 'Delivery Food', daysAgo: 2 },
  { category: 'Food & Dining', amount: 280, type: 'expense', description: 'Coffee & Snacks', daysAgo: 1 },

  // Transportation
  { category: 'Transportation', amount: 2000, type: 'expense', description: 'Car Fuel', daysAgo: 3 },
  { category: 'Transportation', amount: 1500, type: 'expense', description: 'Car Maintenance', daysAgo: 15 },
  { category: 'Transportation', amount: 500, type: 'expense', description: 'Parking Fee', daysAgo: 1 },
  { category: 'Transportation', amount: 8500, type: 'expense', description: 'Car Insurance Premium', daysAgo: 20 },
  { category: 'Transportation', amount: 350, type: 'expense', description: 'Uber Ride', daysAgo: 5 },

  // Health & Personal Care
  { category: 'Health & Personal Care', amount: 2000, type: 'expense', description: 'Doctor Visit', daysAgo: 8 },
  { category: 'Health & Personal Care', amount: 800, type: 'expense', description: 'Medicines & Supplements', daysAgo: 9 },
  { category: 'Health & Personal Care', amount: 600, type: 'expense', description: 'Gym Membership', daysAgo: 25 },
  { category: 'Health & Personal Care', amount: 450, type: 'expense', description: 'Haircut & Salon', daysAgo: 12 },
  { category: 'Health & Personal Care', amount: 550, type: 'expense', description: 'Skincare Products', daysAgo: 18 },

  // Lifestyle & Entertainment
  { category: 'Lifestyle & Entertainment', amount: 1200, type: 'expense', description: 'Netflix, Prime, Disney+', daysAgo: 22 },
  { category: 'Lifestyle & Entertainment', amount: 3500, type: 'expense', description: 'Laptop Accessories', daysAgo: 11 },
  { category: 'Lifestyle & Entertainment', amount: 2000, type: 'expense', description: 'Gaming Console', daysAgo: 30 },
  { category: 'Lifestyle & Entertainment', amount: 800, type: 'expense', description: 'Movie Tickets', daysAgo: 6 },

  // Travel & Holidays
  { category: 'Travel & Holidays', amount: 8000, type: 'expense', description: 'Flight Booking - Weekend Trip', daysAgo: 45 },
  { category: 'Travel & Holidays', amount: 5000, type: 'expense', description: 'Hotel Stay - 3 nights', daysAgo: 35 },
  { category: 'Travel & Holidays', amount: 2500, type: 'expense', description: 'Vacation Spending', daysAgo: 40 },

  // Education & Family
  { category: 'Education & Family', amount: 50000, type: 'expense', description: 'Annual Tuition Payment', daysAgo: 60 },
  { category: 'Education & Family', amount: 5000, type: 'expense', description: 'Books & Course Materials', daysAgo: 20 },
  { category: 'Education & Family', amount: 800, type: 'expense', description: 'Pet Food & Supplies', daysAgo: 3 },

  // Fees & Mishaps
  { category: 'Fees & Mishaps', amount: 500, type: 'expense', description: 'Bank ATM Fees', daysAgo: 19 },
  { category: 'Fees & Mishaps', amount: 2000, type: 'expense', description: 'Parking Fine', daysAgo: 27 },
  { category: 'Fees & Mishaps', amount: 1500, type: 'expense', description: 'Emergency Phone Repair', daysAgo: 13 },

  // ─── INCOME ─────────────────────────────────────────────
  { category: 'Primary Income', amount: 75000, type: 'income', description: 'Monthly Salary', daysAgo: 28 },
  { category: 'Primary Income', amount: 5000, type: 'income', description: 'Freelance Project Payment', daysAgo: 5 },
  { category: 'Passive & Investment', amount: 1500, type: 'income', description: 'Stock Dividends', daysAgo: 18 },
  { category: 'Passive & Investment', amount: 3000, type: 'income', description: 'Rental Income', daysAgo: 25 },
  { category: 'Passive & Investment', amount: 500, type: 'income', description: 'Interest on Savings', daysAgo: 30 },
  { category: 'Other Income', amount: 2000, type: 'income', description: 'Birthday Gift', daysAgo: 22 },
  { category: 'Other Income', amount: 1200, type: 'income', description: 'Freelance Side Gig', daysAgo: 9 },

  // ─── SAVINGS (Internal Transfers) ────────────────────────
  { category: 'Emergency Fund', amount: 10000, type: 'transfer', description: 'Monthly Emergency Fund Contribution', daysAgo: 5 },
  { category: 'Sinking Funds', amount: 5000, type: 'transfer', description: 'Vacation Fund Top-up', daysAgo: 12 },
  { category: 'Investments', amount: 20000, type: 'transfer', description: 'Monthly Investment Plan', daysAgo: 28 },
  { category: 'Retirement Accounts', amount: 15000, type: 'transfer', description: '401k Monthly Contribution', daysAgo: 27 },

  // ─── TRANSFERS (Credit Card, Withdrawals) ─────────────────
  { category: 'Credit Card Payment', amount: 8500, type: 'transfer', description: 'Credit Card Bill Payment', daysAgo: 4 },
  { category: 'Inter-Account Transfer', amount: 30000, type: 'transfer', description: 'Transfer to Savings Account', daysAgo: 15 },
  { category: 'Cash Withdrawal', amount: 5000, type: 'transfer', description: 'ATM Cash Withdrawal', daysAgo: 2 },
  { category: 'Reimbursements & Splitting', amount: 3000, type: 'transfer', description: 'Roommate Rent Split Payment Received', daysAgo: 8 },
];

const testTransactionAPI = {
  /**
   * Generates and populates test transactions
   * Creates realistic transaction data across different categories and dates
   */
  populateTestTransactions: async () => {
    try {
      const created = [];
      const errors = [];
      const failedCategories = new Set();

      // Get current date for calculating transaction dates
      const today = new Date();

      // Fetch all categories to get their IDs
      let categoriesRes = await categoryAPI.list({ active: 'true' });
      let categories = categoriesRes.data || [];

      // If no categories exist, populate them first
      if (categories.length === 0) {
        // Import testDataAPI dynamically to avoid circular dependency
        const testDataAPI = (await import('./testDataService')).default;
        await testDataAPI.populateTestCategories();
        // Fetch categories again after populating
        categoriesRes = await categoryAPI.list({ active: 'true' });
        categories = categoriesRes.data || [];
      }

      if (categories.length === 0) {
        return {
          success: false,
          created: 0,
          failed: 0,
          errors: ['No categories found. Please create categories first.'],
          message: '❌ No categories found. Populate test categories first via onboarding.',
        };
      }

      const categoryMap = {}; // Map category name to ID
      categories.forEach(cat => {
        categoryMap[cat.name] = cat.id;
      });

      for (const txnTemplate of TEST_TRANSACTIONS) {
        try {
          // Get category ID
          const categoryId = categoryMap[txnTemplate.category];
          if (!categoryId) {
            const categoryName = txnTemplate.category;
            if (!failedCategories.has(categoryName)) {
              errors.push(`Category not found: ${categoryName}`);
              failedCategories.add(categoryName);
            }
            continue;
          }

          // Calculate transaction date
          const transactionDate = new Date(today);
          transactionDate.setDate(transactionDate.getDate() - txnTemplate.daysAgo);

          // Create transaction payload with correct field names
          const txnPayload = {
            date: transactionDate.toISOString().split('T')[0], // YYYY-MM-DD format
            amount: txnTemplate.amount,
            transaction_type: txnTemplate.type.toUpperCase(), // EXPENSE, INCOME, TRANSFER
            category: categoryId, // Category ID, not name
            description: txnTemplate.description,
            notes: `Test transaction - ${txnTemplate.daysAgo} days ago`,
            currency: 'INR',
          };

          // Create transaction via API
          const response = await transactionAPI.create(txnPayload);

          if (response.data && response.data.id) {
            created.push({
              id: response.data.id,
              description: txnTemplate.description,
              amount: txnTemplate.amount,
              category: txnTemplate.category,
            });
          }
        } catch (err) {
          errors.push(`Error: ${err.response?.data?.detail || err.message || 'Failed to create transaction'}`);
        }
      }

      // Calculate summary
      const totalAmount = created.reduce((sum, txn) => sum + txn.amount, 0);
      const incomeTransactions = TEST_TRANSACTIONS.filter(txn => txn.type === 'income' && created.find(c => c.description === txn.description));
      const expenseTransactions = TEST_TRANSACTIONS.filter(txn => txn.type === 'expense' && created.find(c => c.description === txn.description));
      const transferTransactions = TEST_TRANSACTIONS.filter(txn => txn.type === 'transfer' && created.find(c => c.description === txn.description));

      return {
        success: created.length > 0,
        created: created.length,
        failed: errors.length,
        totalTransactions: TEST_TRANSACTIONS.length,
        errors: errors,
        summary: {
          totalAmount: totalAmount.toLocaleString('en-IN', {
            style: 'currency',
            currency: 'INR',
          }),
          incomeCount: incomeTransactions.length,
          expenseCount: expenseTransactions.length,
          transferCount: transferTransactions.length,
        },
        message:
          created.length === TEST_TRANSACTIONS.length
            ? `✅ Created ${created.length} test transactions successfully!`
            : created.length > 0
            ? `⚠️ Created ${created.length}/${TEST_TRANSACTIONS.length} transactions (${errors.length} failed)`
            : `❌ Failed to create test transactions (${errors.length} errors)`,
      };
    } catch (err) {
      return {
        success: false,
        created: 0,
        failed: TEST_TRANSACTIONS.length,
        errors: [err.message || 'Failed to populate test transactions'],
        message: '❌ Error populating test transactions',
      };
    }
  },

  /**
   * Get summary statistics of test transactions
   */
  getSummaryStats: () => {
    let totalIncome = 0;
    let totalExpense = 0;
    let totalTransfer = 0;
    const categoryBreakdown = {};

    TEST_TRANSACTIONS.forEach((txn) => {
      if (txn.type === 'income') {
        totalIncome += txn.amount;
      } else if (txn.type === 'expense') {
        totalExpense += txn.amount;
      } else if (txn.type === 'transfer') {
        totalTransfer += txn.amount;
      }

      if (!categoryBreakdown[txn.category]) {
        categoryBreakdown[txn.category] = 0;
      }
      categoryBreakdown[txn.category] += txn.amount;
    });

    return {
      totalTransactions: TEST_TRANSACTIONS.length,
      totalIncome,
      totalExpense,
      totalTransfer,
      netBalance: totalIncome - totalExpense,
      categoryBreakdown,
      dateRange: {
        oldestDaysAgo: Math.max(...TEST_TRANSACTIONS.map((t) => t.daysAgo)),
        newestDaysAgo: Math.min(...TEST_TRANSACTIONS.map((t) => t.daysAgo)),
      },
    };
  },

  /**
   * Get raw test transaction data (for preview before creation)
   */
  getTestData: () => TEST_TRANSACTIONS,

  /**
   * Get count of test transactions by category
   */
  getCountByCategory: () => {
    const counts = {};
    TEST_TRANSACTIONS.forEach((txn) => {
      counts[txn.category] = (counts[txn.category] || 0) + 1;
    });
    return counts;
  },

  /**
   * Get count of test transactions by type
   */
  getCountByType: () => {
    const counts = { income: 0, expense: 0, transfer: 0 };
    TEST_TRANSACTIONS.forEach((txn) => {
      counts[txn.type]++;
    });
    return counts;
  },
};

export default testTransactionAPI;
